// Core 100 — a one-page cheat sheet of the highest-frequency A1 words, jotted
// like notes on paper and grouped into colour-coded blocks. Pairs with the First
// Words starter (that's the phrases; this is the words underneath). Tap a word to
// hear it; hide the English to quiz yourself.
import { speakButtonHTML } from "../lib/audio.js";

const BLOCKS = [
  { title: "Courtesy & basics", color: "text-sage-700", words: [["ciao", "hi / bye"], ["arrivederci", "goodbye"], ["sì", "yes"], ["no", "no"], ["grazie", "thank you"], ["per favore", "please"], ["scusa", "sorry / excuse me"]] },
  { title: "Question words", color: "text-rose-600", words: [["che / cosa", "what"], ["chi", "who"], ["dove", "where"], ["quando", "when"], ["come", "how"], ["perché", "why"], ["quanto", "how much"], ["quale", "which"]] },
  { title: "Pronouns", color: "text-indigo-600", words: [["io", "I"], ["tu", "you"], ["lui", "he"], ["lei", "she"], ["Lei", "you (formal)"], ["noi", "we"], ["voi", "you all"], ["loro", "they"]] },
  { title: "The big verbs", color: "text-amber-700", words: [["essere", "to be"], ["stare", "to be / stay"], ["avere", "to have"], ["fare", "to do/make"], ["andare", "to go"], ["potere", "to be able"], ["volere", "to want"], ["vedere", "to see"], ["dare", "to give"], ["sapere", "to know"], ["dire", "to say"], ["venire", "to come"]] },
  { title: "Time words", color: "text-sky-700", words: [["oggi", "today"], ["domani", "tomorrow"], ["ieri", "yesterday"], ["adesso", "now"], ["sempre", "always"], ["mai", "never"], ["prima", "before"], ["dopo", "after"], ["presto", "early / soon"], ["tardi", "late"]] },
  { title: "Connectors", color: "text-teal-700", words: [["e", "and"], ["o", "or"], ["ma", "but"], ["perché", "because"], ["anche", "also"], ["se", "if"], ["quando", "when"], ["benché", "although"]] },
  { title: "Prepositions", color: "text-violet-700", words: [["di", "of / from"], ["a", "to / at"], ["da", "from / by"], ["in", "in / to"], ["con", "with"], ["per", "for / through"], ["su", "on / about"], ["tra", "between"], ["senza", "without"], ["fino a", "until"]] },
  { title: "Everyday nouns", color: "text-stone-600", words: [["la casa", "house"], ["l'acqua", "water"], ["il giorno", "day"], ["il tempo", "time / weather"], ["la persona", "person"], ["la cosa", "thing"], ["l'uomo", "man"], ["la donna", "woman"], ["l'anno", "year"], ["la vita", "life"]] },
  { title: "Useful adjectives", color: "text-emerald-700", words: [["buono", "good"], ["cattivo", "bad"], ["grande", "big"], ["piccolo", "small"], ["nuovo", "new"], ["molto", "a lot"], ["poco", "a little"], ["facile", "easy"], ["difficile", "hard"], ["stesso", "same"]] },
  { title: "Little words", color: "text-sage-700", words: [["molto", "very"], ["più", "more"], ["meno", "less"], ["bene", "well"], ["male", "badly"], ["qui", "here"], ["lì", "there"], ["così", "like this"]] },
  { title: "Numbers 1–10", color: "text-rose-600", words: [["uno", "1"], ["due", "2"], ["tre", "3"], ["quattro", "4"], ["cinque", "5"], ["sei", "6"], ["sette", "7"], ["otto", "8"], ["nove", "9"], ["dieci", "10"]] },
];

function blockHTML(b) {
  const rows = b.words
    .map(
      ([es, en]) => `
      <div class="flex items-baseline justify-between gap-2 py-0.5">
        <span class="text-sage-800">${es}${speakButtonHTML(es.replace(/^(el|la) /, ""))}</span>
        <span class="c100-en text-sm text-sage-500 text-right">${en}</span>
      </div>`
    )
    .join("");
  return `
    <div class="sketch-box-sm bg-white/30 p-3 mb-4 break-inside-avoid">
      <h3 class="${b.color} text-xl sketch-underline inline-block mb-1">${b.title} <span class="opacity-50 text-sm">· ${b.words.length}</span></h3>
      <div>${rows}</div>
    </div>`;
}

export function core100View(app) {
  const total = BLOCKS.reduce((n, b) => n + b.words.length, 0);
  app.innerHTML = `
    <section class="max-w-4xl mx-auto fade-in">
      <div class="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h2 class="text-4xl font-script text-sage-800">Core ${total} Words 🗺️</h2>
        <button id="c100-toggle" class="text-sm px-3 py-1.5 rounded-full border border-sage-200 text-sage-600 hover:border-sage-400" style="font-family:Inter,sans-serif">Hide English</button>
      </div>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">The highest-frequency words in Italian, grouped so you find and revisit them fast. Tap 🔊 to hear a word; hide the English to test yourself.</p>
      <div class="paper sketch-box p-4 sm:p-6 font-hand text-lg">
        <div class="columns-1 sm:columns-2 lg:columns-3 gap-4">
          ${BLOCKS.map(blockHTML).join("")}
        </div>
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Nouns show their article (il/la/lo/l') so you learn the gender with the word.</p>
    </section>`;

  const sec = app.querySelector("section");
  const btn = app.querySelector("#c100-toggle");
  let hidden = false;
  btn.addEventListener("click", () => {
    hidden = !hidden;
    sec.querySelectorAll(".c100-en").forEach((e) => e.classList.toggle("invisible", hidden));
    btn.textContent = hidden ? "Show English" : "Hide English";
  });
  window.scrollTo({ top: 0, behavior: "smooth" });
}
